import type React from "react";
import classNames from "classnames";
import {
    anatomyPartValue,
    type AnatomyPartId,
    type ResolvedAnatomyBinding,
} from "./componentAnatomy";
import styles from "./ComponentAnatomyView.module.scss";

/**
 * Primitives shared by the anatomy schematics and legend: the hover-highlight
 * contract, a reader for one resolved binding, and the layer element that turns
 * a part into its own highlightable box.
 */

export interface AnatomyPartHighlight {
    activePartId: AnatomyPartId | null;
    onPartActivate: (partId: AnatomyPartId | null) => void;
}

export const bindingReader =
    (bindings: readonly ResolvedAnatomyBinding[]) =>
    (partId: AnatomyPartId, property: string) =>
        anatomyPartValue({ bindings, partId, property });

/**
 * Layers paint in stacking order, so a layer sitting under another can only be
 * hovered where nothing above covers it. `hitInset` moves a part's hit area off
 * its paint box for the two cases where those differ: a ring painted outside its
 * box (negative inset, reaching the band it actually draws in) and a glyph whose
 * box is larger than the vector inside it (positive inset, handing the
 * surrounding pixels back to the fill underneath).
 */
export const Part = ({
    partId,
    highlight,
    className,
    style,
    hitInset,
    children,
}: {
    partId: AnatomyPartId;
    highlight: AnatomyPartHighlight;
    className?: string;
    style?: React.CSSProperties;
    hitInset?: string;
    children?: React.ReactNode;
}) => (
    <span
        aria-hidden="true"
        className={classNames(styles.part, className, {
            [styles.partActive]: highlight.activePartId === partId,
            [styles.partMuted]:
                highlight.activePartId !== null &&
                highlight.activePartId !== partId,
            [styles.partHitDelegated]: hitInset !== undefined,
        })}
        style={style}
        onMouseEnter={() => highlight.onPartActivate(partId)}
        onMouseLeave={() => highlight.onPartActivate(null)}
    >
        {hitInset !== undefined && (
            <span className={styles.partHitArea} style={{ inset: hitInset }} />
        )}
        {children}
    </span>
);
